import { ReactElement, useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { CSVUploadFormData } from "../types/form-inputs";
import { Button } from "./catalyst/Button";
import { Heading } from "./catalyst/Heading";
import { Text } from "./catalyst/Text";
import InputFieldGroup from "./inputs/InputFieldGroup";
import SelectFieldGroup from "./inputs/SelectFieldGroup";

export const CSVRelationshipConfigurator = (): ReactElement => {
  const {
    control,
    formState: { errors },
    register,
    trigger,
    watch,
  } = useFormContext<CSVUploadFormData>();
  const { fields: columnMappings } = useFieldArray({
    control,
    name: "columnsMappings",
  });
  const {
    append,
    fields: relationships,
    remove,
  } = useFieldArray({
    control,
    name: "relationships",
  });

  const watchedRelationships = watch("relationships");

  useEffect(() => {
    relationships.forEach((_, index) => {
      const relationship = watchedRelationships?.[index];
      if (relationship?.fromEntityId && relationship?.toEntityId) {
        trigger(`relationships.${index}.toEntityId`);
      }
    });
  }, [watchedRelationships, trigger, relationships]);

  const handleAddRelationship = (): void => {
    append({
      fromEntityId: "",
      name: "",
      toEntityId: "",
    });
  };

  // Filter out ignored columns and those without entityId for relationship options
  const availableColumns = columnMappings.filter((mapping) => mapping.entityId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Heading level={3}>Relationships</Heading>
        <Button onClick={handleAddRelationship} size="xs">
          Add Relationship
        </Button>
      </div>
      <Text className="text-sm text-zinc-600 dark:text-zinc-400">
        Configure relationships between entities in your CSV.
      </Text>

      <div className="space-y-3">
        {relationships.map((_relationship, index) => (
          <div
            className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700"
            key={index}
          >
            <div className="mb-3 flex items-center justify-between">
              <Text className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Relationship {index + 1}
              </Text>
              <Button color="red" onClick={() => remove(index)} size="xs">
                Remove
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-1">
                <SelectFieldGroup<CSVUploadFormData>
                  errors={errors}
                  fieldName={`relationships.${index}.fromEntityId`}
                  label="Source Column"
                  options={availableColumns.map((mapping) => ({
                    label: `${mapping.name}`,
                    value: mapping.entityId!,
                  }))}
                  register={register}
                />
              </div>

              <div className="col-span-1">
                <SelectFieldGroup<CSVUploadFormData>
                  errors={errors}
                  fieldName={`relationships.${index}.toEntityId`}
                  label="Target Column"
                  options={availableColumns.map((mapping) => ({
                    label: `${mapping.name}`,
                    value: mapping.entityId!,
                  }))}
                  register={register}
                  rules={{
                    required: "Target column is required",
                    validate: (value, formValues) => {
                      const sourceValue =
                        formValues.relationships?.[index]?.fromEntityId;
                      if (value === sourceValue) {
                        return "Source and target columns cannot be the same";
                      }
                      return true;
                    },
                  }}
                />
              </div>

              <div className="col-span-2">
                <InputFieldGroup<CSVUploadFormData>
                  errors={errors}
                  fieldName={`relationships.${index}.name`}
                  label="Relationship Type"
                  multiline={false}
                  placeHolder="Enter a description for this relationship"
                  register={register}
                  rules={{
                    required: "Relationship type is required",
                  }}
                />
              </div>
            </div>
          </div>
        ))}

        {relationships.length === 0 && (
          <div className="py-8 text-center text-zinc-500">
            <Text>No relationships configured yet.</Text>
            <Text className="text-sm">
              Click "Add Relationship" to get started.
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};
